<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-slate-50 dark:bg-slate-900"
  >
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
    />



    <div class="flex flex-col flex-1 min-w-0 w-full">
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="max-w-6xl mx-auto">
          <!-- header -->
          <div
            class="flex flex-col justify-between gap-3 mb-4 sm:flex-row sm:items-center"
          >
            <div>
              <h1 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ pageTitle }}
              </h1>
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{ pageSubtitle }}
              </p>
            </div>
          </div>

          <section
            class="overflow-hidden bg-white border shadow-sm dark:bg-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl"
          >
            <!-- Tabs -->
            <header
              class="flex items-center justify-between px-4 pt-4 pb-2 border-b border-slate-100 dark:border-slate-800"
            >
              <div
                class="inline-flex items-center gap-2 p-1 rounded-full bg-slate-50 dark:bg-slate-800"
              >
                <button
                  type="button"
                  @click="activeTab = 'account'"
                  class="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-medium rounded-full transition shadow-sm"
                  :class="
                    activeTab === 'account'
                      ? 'bg-primary text-white'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/80 dark:hover:bg-slate-700/60'
                  "
                >
                  <span
                    class="inline-flex items-center justify-center w-5 h-5 text-[13px] rounded-full"
                    :class="
                      activeTab === 'account'
                        ? 'bg-secondary/50 text-white'
                        : 'bg-secondary/10 text-primary'
                    "
                  >
                    <i class="mdi mdi-account-outline"></i>
                  </span>
                  <span>{{ t("account_tab_account") }}</span>
                </button>

                <button
                  type="button"
                  @click="activeTab = 'security'"
                  class="inline-flex items-center gap-2 px-4 py-1.5 text-xs font-medium rounded-full transition"
                  :class="
                    activeTab === 'security'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/80 dark:hover:bg-slate-700/60'
                  "
                >
                  <span
                    class="inline-flex items-center justify-center w-5 h-5 text-[13px] rounded-full"
                    :class="
                      activeTab === 'security'
                        ? 'bg-secondary/50 text-white'
                        : 'bg-secondary/10 text-primary'
                    "
                  >
                    <i class="mdi mdi-lock-outline"></i>
                  </span>
                  <span>{{ t("account_tab_security") }}</span>
                </button>
              </div>

              <span class="hidden text-xs text-slate-400 sm:inline">
                {{ t("account_last_updated_hint") }}
              </span>
            </header>

            <!-- ACCOUNT -->
            <form
              v-if="activeTab === 'account'"
              class="space-y-6"
              @submit.prevent="saveSettings"
            >
              <!-- ✅ You can keep your same sections markup here -->
              <!-- For brevity: keep your full form UI exactly as you have it -->
              <!-- Just bind to form.consultant / form.address / form.bank as below -->

              <!-- Consultant section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-briefcase-outline text-secondary"></i>
                  <span>{{
                    t("consultant_section_profile") || "Consultant Profile"
                  }}</span>
                  <span class="text-rose-500">*</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div
                    class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
                  >
                    <!-- ✅ Profile Image (nice) - put FIRST -->
                    <div class="md:col-span-2 lg:col-span-3">
                      <label
                        class="block mb-2 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("consultant_field_image") || "Profile Image" }}
                      </label>

                      <div
                        class="flex flex-col gap-4 p-4 border bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-2xl sm:flex-row sm:items-center"
                      >
                        <!-- Preview -->
                        <div class="flex items-center gap-3">
                          <div
                            class="relative w-16 h-16 overflow-hidden border rounded-2xl bg-white/70 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                          >
                            <img
                              v-if="form.consultant.logo_url"
                              :src="form.consultant.logo_url"
                              alt="profile"
                              class="object-cover w-full h-full"
                            />
                            <div
                              v-else
                              class="flex items-center justify-center w-full h-full text-slate-400"
                            >
                              <i
                                class="mdi mdi-account-circle-outline text-[34px]"
                              ></i>
                            </div>

                            <!-- tiny badge -->
                            <span
                              class="absolute flex items-center justify-center w-6 h-6 text-white rounded-full -bottom-2 -right-2 bg-primary"
                            >
                              <i class="mdi mdi-camera-outline text-[14px]"></i>
                            </span>
                          </div>

                          <div class="min-w-0">
                            <p
                              class="text-sm font-semibold text-slate-800 dark:text-slate-100"
                            >
                              {{
                                t("consultant_field_image") || "Profile Image"
                              }}
                            </p>
                            <p
                              class="text-xs text-slate-500 dark:text-slate-400"
                            >
                              {{ t("upload_image_hint") || "PNG, JPG حتى 2" }}
                            </p>

                            <p
                              class="mt-1 text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-[220px]"
                            >
                              {{
                                form.consultant.image_meta?.name
                                  ? form.consultant.image_meta.name
                                  : t("no_file_chosen") || "No file chosen"
                              }}
                            </p>
                          </div>
                        </div>

                        <!-- Actions -->
                        <div class="flex flex-wrap gap-2 sm:ms-auto">
                          <!-- Upload -->
                          <label
                            class="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium text-white shadow-sm cursor-pointer rounded-xl bg-primary hover:bg-primary/90"
                          >
                            <i class="mdi mdi-upload"></i>
                            {{ t("choose_file") || "Choose file" }}
                            <input
                              type="file"
                              accept="image/*"
                              class="hidden"
                              @change="onImageChange"
                            />
                          </label>

                          <!-- Remove -->
                          <!-- <button
                            v-if="form.consultant.logo_url"
                            type="button"
                            class="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium bg-white border rounded-xl border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800"
                            @click="clearProfileImage"
                          >
                            <i class="mdi mdi-close-circle-outline"></i>
                            {{ t("remove") || "Remove" }}
                          </button> -->
                        </div>
                      </div>
                    </div>

                    <!-- Name -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("consultant_field_name") || "Name" }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-account-outline text-[18px]"></i>
                        </span>

                        <input
                          v-model="form.consultant.name"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('consultant_name_ph')"
                        />

                        <p
                          v-if="errors?.consultant?.name"
                          class="mt-1 text-xs text-red-500"
                        >
                          {{ errors.consultant.name[0] }}
                        </p>
                      </div>
                    </div>

                    <!-- Specialization -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{
                          t("consultant_field_specialization") ||
                          "Specialization"
                        }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-star-outline text-[18px]"></i>
                        </span>

                        <input
                          v-model="form.consultant.specialization"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('specialization_ph')"
                        />
                      </div>
                    </div>

                    <!-- License number -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{
                          t("consultant_field_license_number") ||
                          "License Number"
                        }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-card-account-details-outline text-[18px]"
                          ></i>
                        </span>

                        <input
                          v-model="form.consultant.license_number"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('license_no_ph')"
                        />
                      </div>
                    </div>

                    <!-- Qualification -->
                    <div class="md:col-span-2 lg:col-span-2">
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{
                          t("consultant_field_qualification") || "Qualification"
                        }}
                      </label>

                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-school-outline text-[18px]"></i>
                        </span>

                        <input
                          v-model="form.consultant.qualification"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('degree_ph')"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- ✅ Address Section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-map-marker-outline text-secondary"></i>
                  <span>{{
                    t("consultant_section_address") || "Address"
                  }}</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- City -->

                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_city") || "City" }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-city-variant-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.address.city"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_city_ph')"
                        />
                      </div>
                    </div>

                    <!-- District -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_district") || "District" }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-map-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.district"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_district_ph')"
                        />
                      </div>
                    </div>

                    <!-- Street -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_street") || "Street" }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-sign-direction text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.street"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_street_ph')"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- Building -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_building") || "Building Number" }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-office-building-marker-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.address.building"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_building_number_ph')"
                        />
                      </div>
                    </div>

                    <!-- Additional -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_additional") || "Additional Number" }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-pound text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.additional"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_additional_number_ph')"
                        />
                      </div>
                    </div>

                    <!-- Postal -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("address_postal") || "Postal Code" }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-mailbox-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.address.postalCode"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('address_postal_code_ph')"
                        />
                      </div>
                    </div>
                  </div>

                  <!-- Short address -->
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ t("address_short") || "Short Address" }}
                    </label>
                    <div class="relative">
                      <span
                        class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                        :class="
                          lang === 'ar'
                            ? 'right-0 rounded-r-md'
                            : 'left-0 rounded-l-md'
                        "
                      >
                        <i class="mdi mdi-home-map-marker text-[18px]"></i>
                      </span>
                      <input
                        v-model="form.address.shortAddress"
                        type="text"
                        class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                        :placeholder="t('address_short_address_ph')"
                      />
                    </div>
                  </div>
                </div>
              </section>

              <!-- ✅ Bank Section -->
              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-bank-outline text-secondary"></i>
                  <span>{{
                    t("consultant_section_bank") || "Bank Account"
                  }}</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <!-- Holder -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("bank_holder") || "Account Holder" }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-account-outline text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.bank.holder"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('bank_owner')"
                        />
                      </div>
                    </div>

                    <!-- IBAN -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("bank_iban") || "IBAN / Account Number" }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i
                            class="mdi mdi-credit-card-outline text-[18px]"
                          ></i>
                        </span>
                        <input
                          v-model="form.bank.iban"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('iban_ph')"
                        />
                      </div>
                    </div>

                    <!-- Bank Name -->
                    <div>
                      <label
                        class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                      >
                        {{ t("bank_name") || "Bank Name" }}
                      </label>
                      <div class="relative">
                        <span
                          class="absolute inset-y-0 flex items-center justify-center w-8 bg-secondary/15 text-primary"
                          :class="
                            lang === 'ar'
                              ? 'right-0 rounded-r-md'
                              : 'left-0 rounded-l-md'
                          "
                        >
                          <i class="mdi mdi-bank text-[18px]"></i>
                        </span>
                        <input
                          v-model="form.bank.name"
                          type="text"
                          class="w-full py-2 text-sm border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          :class="lang === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'"
                          :placeholder="t('bank_name')"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <div
                  class="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b bg-slate-50/90 dark:bg-slate-800/80 border-slate-100 dark:border-slate-800"
                >
                  <i class="mdi mdi-paperclip text-secondary"></i>
                  <span>{{
                    t("consultant_section_attachments") || "Attachments"
                  }}</span>
                </div>

                <div class="px-5 py-4 space-y-4">
                  <!-- Existing attachments -->
                  <div
                    v-if="(form.consultant.existing_attachments || []).length"
                    class="space-y-2"
                  >
                    <div
                      class="text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{
                        t("consultant_existing_attachments") ||
                        "Existing Attachments"
                      }}
                    </div>

                    <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
                      <div
                        v-for="(att, i) in form.consultant.existing_attachments"
                        :key="(att.id || att.url || 'att') + '_' + i"
                        class="p-3 border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                      >
                        <div class="flex items-center justify-between gap-2">
                          <div class="min-w-0">
                            <!-- اختياري: سطر صغير يوضح نوع الملف -->
                            <p class="text-[11px] truncate text-slate-400">
                              {{ getFilename(att.url) }}
                            </p>
                          </div>

                          <div class="flex items-center gap-2 shrink-0">
                            <!-- ✅ View button -->
                            <a
                              :href="absoluteUrl(att.url)"
                              target="_blank"
                              class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white rounded bg-primary hover:bg-primary/90"
                              rel="noopener"
                            >
                              <i class="mdi mdi-eye-outline"></i>
                              {{ t("view") || "View" }}
                            </a>
                            <!-- ✅ Upload New button -->
                            <!-- <label
                              class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                              :title="t('upload_new') || 'Upload new file'"
                            >
                              <i class="mdi mdi-upload"></i>
                              {{ t("choose_file") || "Choose File" }}

                              <input
                                type="file"
                                class="hidden"
                                accept="image/*,.pdf"
                                @change="
                                  (e) => replaceExistingAttachmentFile(att, e)
                                "
                              />
                            </label> -->

                            <button
                              type="button"
                              class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white rounded bg-rose-500 hover:bg-rose-600"
                              @click="removeExistingAttachment(att)"
                            >
                              <i class="mdi mdi-delete-outline"></i>
                              {{ t("remove") || "Remove" }}
                            </button>
                          </div>
                        </div>

                        <input
                          v-model="att.title"
                          type="text"
                          class="w-full px-3 py-2 mt-2 text-xs bg-white border rounded-lg dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                          :placeholder="
                            t('account_section_address') || 'Title...'
                          "
                        />
                        <div
                          v-if="
                            att && att._replacement && att._replacement.file
                          "
                          class="p-2 mt-2 border rounded bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                        >
                          <p
                            class="text-xs truncate text-slate-700 dark:text-slate-200"
                          >
                            New file: {{ att._replacement.file.name }}
                          </p>

                          <button
                            type="button"
                            class="mt-1 text-[11px] text-rose-600 hover:underline"
                            @click="clearReplacement(att)"
                          >
                            {{ t("cancel") || "Cancel replacement" }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- New attachments -->
                  <div>
                    <div
                      class="mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{
                        t("consultant_new_attachments") || "Add New Attachments"
                      }}
                    </div>

                    <div
                      class="flex items-center gap-3 p-3 border rounded-lg bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                    >
                      <!-- Button -->
                      <label
                        class="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium text-white rounded-lg cursor-pointer bg-primary hover:bg-primary/90"
                      >
                        <i class="mdi mdi-upload-multiple"></i>
                        {{ t("choose_files") || "Choose files" }}

                        <input
                          type="file"
                          multiple
                          accept="image/*,.pdf"
                          class="hidden"
                          @change="onAttachmentsChange"
                        />
                      </label>

                      <!-- Count -->
                      <div class="flex-1 min-w-0">
                        <p
                          class="text-xs font-medium text-slate-700 dark:text-slate-200"
                        >
                          {{ (form.consultant.attachments || []).length }}
                          {{ t("files_selected") || "files selected" }}
                        </p>
                        <p class="text-[11px] text-slate-400">Images or PDF</p>
                      </div>
                    </div>

                    <!-- Selected list + title -->
                    <div
                      v-if="(form.consultant.attachments || []).length"
                      class="mt-2 space-y-2"
                    >
                      <div
                        v-for="(att, i) in form.consultant.attachments"
                        :key="att.file?.name + '_' + att.file?.size + '_' + i"
                        class="p-3 bg-white border rounded-lg dark:bg-slate-900"
                      >
                        <div class="flex items-center justify-between gap-2">
                          <div
                            class="text-xs truncate text-slate-600 dark:text-slate-300"
                          >
                            {{ att.file?.name || "—" }}
                          </div>
                          <button
                            type="button"
                            @click="removeNewAttachment(i)"
                            class="px-2 py-1 text-xs text-white rounded bg-slate-600"
                          >
                            Remove
                          </button>
                        </div>

                        <div class="mt-2">
                          <label
                            class="block mb-1 text-[11px] font-medium text-slate-600 dark:text-slate-300"
                          >
                            {{ t("account_section_address") || "Title" }}
                          </label>

                          <input
                            v-model="att.title"
                            type="text"
                            class="w-full px-3 py-2 text-xs border rounded-lg bg-slate-50 dark:bg-slate-800"
                            placeholder="e.g. License, CV, Certificate..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Actions -->
              <footer
                class="flex flex-col items-stretch gap-3 px-5 py-4 border-t border-slate-100 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60"
              >
                <div class="flex gap-2">
                  <button
                    type="submit"
                    :disabled="saving"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm bg-primary hover:bg-primary/90 disabled:opacity-60"
                  >
                    <i v-if="saving" class="mdi mdi-loading mdi-spin me-2"></i>
                    {{
                      saving ? t("account_btn_saving") : t("account_btn_save")
                    }}
                  </button>
                </div>

                <p class="text-[11px] text-slate-400">
                  {{ t("account_required_fields_note") }}
                </p>
              </footer>
            </form>

            <!-- SECURITY -->
            <form v-else class="space-y-6" @submit.prevent="savePassword">
              <!-- keep your security form as-is -->
              <div class="p-5">
                <div class="text-sm text-slate-700 dark:text-slate-200">
                  {{ t("security_section_title") }}
                </div>

                <div class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2">
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ t("security_current_password") }}
                    </label>
                    <input
                      v-model="passwordForm.current_password"
                      type="password"
                      class="w-full px-3 py-2 border rounded-lg"
                      :placeholder="t('security_current_password_placeholder')"
                    />
                  </div>
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ t("security_new_password") }}
                    </label>
                    <input
                      v-model="passwordForm.password"
                      type="password"
                      class="w-full px-3 py-2 border rounded-lg"
                      :placeholder="t('security_new_password_placeholder')"
                    />
                  </div>
                  <div>
                    <label
                      class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                      {{ t("security_confirm_password") }}
                    </label>
                    <input
                      v-model="passwordForm.password_confirmation"
                      type="password"
                      class="w-full px-3 py-2 border rounded-lg"
                      :placeholder="t('security_confirm_password_placeholder')"
                    />
                  </div>
                </div>

                <div class="mt-4">
                  <button
                    type="submit"
                    :disabled="passwordSaving"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm bg-primary hover:bg-primary/90 disabled:opacity-60"
                  >
                    <i
                      v-if="passwordSaving"
                      class="mdi mdi-loading mdi-spin me-2"
                    ></i>
                    {{
                      passwordSaving
                        ? t("security_btn_saving")
                        : t("security_btn_save")
                    }}
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>

        <Toast
          :show="toast.show"
          :type="toast.type"
          :message="toast.message"
          @update:show="toast.show = $event"
        />
      </main>
    </div>
  </div>
</template>

<script>
import { messages } from "@/i18n";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";
import Toast from "@/components/Toast.vue";
import api from "@/services/api";
import { translateCookieToastMessage } from "@/utils/cookieErrors";

export default {
  name: "AccountSettingsBase",
  components: { AdminSidebar, AdminTopbar, Toast },

  props: {
    userType: {
      type: String,
      required: true, // "client" | "consultant" | "office"
    },
  },

  data() {
    const initial = (
      document.documentElement.lang ||
      localStorage.getItem("app_lang") ||
      "ar"
    ).toLowerCase();

    return {
      messages,
      lang: initial.startsWith("ar") ? "ar" : "en",

      isSidebarCollapsed: false,
      isSidebarOpenMobile: false,
      sidebarSearch: "",

      saving: false,
      loading: true,
      activeTab: "account",

      passwordSaving: false,
      passwordForm: {
        current_password: "",
        password: "",
        password_confirmation: "",
      },

      toast: { show: false, type: "success", message: "" },

      errors: { consultant: {}, address: {}, bank: {} },

      user: null,
      hydratedOnce: false,

      form: {
        consultant: {
          name: "",
          specialization: "",
          license_number: "",
          qualification: "",
          consultant_type: "",
          logo_url: "",
          image_base64: "",
          image_meta: { name: "", type: "", size: 0 },

          attachments: [],
          existing_attachments: [],
          removed_existing_attachments: [],
        },

        address: {
          city: "",
          district: "",
          street: "",
          building: "",
          additional: "",
          postalCode: "",
          shortAddress: "",
        },

        bank: { holder: "", iban: "", name: "" },
      },
    };
  },

  computed: {
    isClient() {
      return this.userType === "client";
    },
    isConsultant() {
      return this.userType === "consultant";
    },
    isOffice() {
      return this.userType === "office";
    },

    pageTitle() {
      if (this.isConsultant)
        return this.t("consultant_settings_title") || "Consultant Settings";
      if (this.isOffice)
        return this.t("office_settings_title") || "Office Settings";
      return this.t("account_settings_title") || "Account Settings";
    },

    pageSubtitle() {
      if (this.isConsultant)
        return (
          this.t("consultant_settings_subtitle") ||
          "Update your consultant profile."
        );
      if (this.isOffice)
        return (
          this.t("office_settings_subtitle") || "Update your office profile."
        );
      return (
        this.t("account_settings_subtitle") ||
        "Update your account information."
      );
    },

    // ✅ robust consultant id
    consultantId() {
      const p = this.$route?.params || {};

      // try common param names
      const raw =
        p.id ??
        p.consultantId ??
        p.consultant_id ??
        p.consultantID ??
        p.profileId ??
        null;

      if (raw != null && String(raw).trim() !== "") return Number(raw);

      // fallback: parse from path /consultants/42
      const path = this.$route?.path || "";
      const m = path.match(/\/consultants\/(\d+)/);
      if (m && m[1]) return Number(m[1]);

      // fallback: cookie/localStorage (optional)
      const cookieId = this.getConsultantIdFromAuthCookie();
      if (cookieId) return Number(cookieId);

      return 0;
    },

    // ✅ SHOW DATA via Approve API
    approveShowEndpoints() {
      if (!this.consultantId) return [];
      return [`/consultants/${this.consultantId}`];
    },
  },

  async mounted() {
    // keep lang reactive with global switcher + html lang/dir changes
    this._langHandler = (e) => {
      const next = (e.detail || "ar").toLowerCase().startsWith("en")
        ? "en"
        : "ar";
      if (next !== this.lang) this.lang = next;
    };
    window.addEventListener("app:lang", this._langHandler, { passive: true });

    this._langObserver = new MutationObserver((muts) => {
      for (const m of muts) {
        if (
          m.type === "attributes" &&
          (m.attributeName === "lang" || m.attributeName === "dir")
        ) {
          const htmlLang = (
            document.documentElement.lang || "ar"
          ).toLowerCase();
          this.lang = htmlLang.startsWith("en") ? "en" : "ar";
        }
      }
    });

    this._langObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang", "dir"],
    });

    const htmlLang = (document.documentElement.lang || "ar").toLowerCase();
    this.lang = htmlLang.startsWith("en") ? "en" : "ar";

    await this.initUserFromApi();
  },

  beforeUnmount() {
    if (this._langObserver) this._langObserver.disconnect();
    if (this._langHandler)
      window.removeEventListener("app:lang", this._langHandler);
  },

  watch: {
    lang: {
      immediate: true,
      handler(next) {
        const safe = next === "en" ? "en" : "ar";
        document.documentElement.lang = safe;
        document.documentElement.dir = safe === "ar" ? "rtl" : "ltr";
      },
    },
    // ✅ if route changes and id arrives later
    "$route.fullPath": {
      handler() {
        if (this.isConsultant && !this.user) this.initUserFromApi();
      },
      immediate: false,
    },
  },

  methods: {
    t(key) {
      return (this.messages[this.lang] && this.messages[this.lang][key]) || key;
    },

    toggleSidebar() {
      if (window.innerWidth < 768)
        this.isSidebarOpenMobile = !this.isSidebarOpenMobile;
      else this.isSidebarCollapsed = !this.isSidebarCollapsed;
    },

    showToast(message, type = "success") {
      this.toast.message = translateCookieToastMessage(message, this.t.bind(this));
      this.toast.type = type;
      this.toast.show = true;
      setTimeout(() => (this.toast.show = false), 2500);
    },

    // ✅ helper: try endpoints until one works (and log which one worked)
    async fetchFirstOk(endpoints) {
      let lastErr = null;

      for (const url of endpoints) {
        try {
          const res = await api.get(url, {
            headers: { Accept: "application/json" },
          });

          return res;
        } catch (e) {
          lastErr = e;
        }
      }

      throw lastErr || new Error("All approve endpoints failed");
    },

    // ✅ main loader
    async initUserFromApi() {
      this.loading = true;

      try {
        // ✅ Consultant page => SHOW via Approve API
        if (this.isConsultant) {
          if (!this.consultantId) {
            this.showToast(
              "Consultant ID not found in route params/path",
              "error"
            );
            return;
          }

          const res = await this.fetchFirstOk(this.approveShowEndpoints);

          const payload = res.data?.data ?? res.data;

          const normalized = payload?.consultant ?? payload;

          this.user = normalized;
          this.fillFormFromUser(true);
          return;
        }

        // ✅ non-consultant fallback (/my-like)
        const meRes = await api.get("/clients", {
          headers: { Accept: "application/json" },
        });

        const me = meRes.data?.data ?? meRes.data;
        if (this.isOffice) this.user = me.office ?? me;
        else this.user = me.client ?? me;
        this.fillFormFromUser(true);
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          this.t("account_saved_failed");

        this.showToast(msg, "error");
      } finally {
        this.loading = false;
      }
    },

    fillFormFromUser(force = false) {
      if (this.hydratedOnce && !force) return;
      if (!this.user) return;

      this.form.consultant.name = this.user?.name ?? "";
      this.form.consultant.specialization = this.user?.specialization ?? "";
      this.form.consultant.license_number = this.user?.license_number ?? "";
      this.form.consultant.qualification = this.user?.qualification ?? "";
      this.form.consultant.consultant_type = this.user?.consultant_type ?? "";
      this.form.consultant.logo_url = this.user?.logo ?? "";
      const rawExisting =
        this.user?.existing_attachments ??
        this.user?.attachments ??
        this.user?.consultant?.existing_attachments ??
        this.user?.consultant?.attachments ??
        this.user?.office?.attachments ??
        [];

      this.form.consultant.existing_attachments = (rawExisting || [])
        .map(this.normalizeExistingAttachment)
        .filter((a) => a.url);

      this.form.consultant.removed_existing_attachments = [];

      const addr = this.user?.address ?? null;
      this.form.address.city = addr?.city ?? "";
      this.form.address.district = addr?.district ?? "";
      this.form.address.street = addr?.street ?? "";
      this.form.address.building = addr?.building_number ?? "";
      this.form.address.additional = addr?.additional_number ?? "";
      this.form.address.postalCode = addr?.postal_code ?? "";
      this.form.address.shortAddress = addr?.short_address ?? "";

      const bank = this.user?.bank_account ?? this.user?.bank ?? null;
      this.form.bank.holder = bank?.account_holder_name ?? "";
      this.form.bank.iban = bank?.account_number ?? "";
      this.form.bank.name = bank?.bank_name ?? "";

      this.hydratedOnce = true;
    },

    setAttachmentTitle(index, value) {
      this.form.consultant.attachments[index].title = value;
    },

    replaceExistingAttachmentFile(att, e) {
      const file = e?.target?.files?.[0];
      if (!file || !att) return;

      att._replacement = {
        file,
        title: att.title || file.name,
      };

      e.target.value = "";
    },

    clearReplacement(att) {
      if (!att) return;
      att._replacement = null;
    },

    async saveSettings() {
      if (this.loading) return;
      this.saving = true;

      try {
        // -------------------------------------------------------
        // 1) Collect NEW attachments (Add New)
        // -------------------------------------------------------
        const attachmentsEncoded = [];

        for (const item of this.form.consultant.attachments || []) {
          if (!item?.file) continue;

          const encoded = await this.fileToBase64(item.file);

          attachmentsEncoded.push({
            ...encoded,
            name: item.title || encoded.name || item.file?.name || "attachment",
          });
        }

        // -------------------------------------------------------
        // 2) Handle REPLACEMENTS for existing attachments
        //    replacement = upload new file + mark old url for removal
        // -------------------------------------------------------
        const removed = [
          ...(this.form.consultant.removed_existing_attachments || []),
        ];

        for (const att of this.form.consultant.existing_attachments || []) {
          const repFile = att?._replacement?.file;
          if (!repFile) continue;

          const encoded = await this.fileToBase64(repFile);

          attachmentsEncoded.push({
            ...encoded,
            name:
              att?._replacement?.title ||
              att?.title ||
              encoded.name ||
              repFile?.name ||
              "attachment",
          });

          if (att?.url && !removed.includes(att.url)) removed.push(att.url);
        }

        const existingTitlesUpdates = (
          this.form.consultant.existing_attachments || []
        )
          .filter((a) => a?.url && a?.title != null)
          .map((a) => ({
            url: a.url,
            title: a.title,
            id: a.id ?? null,
          }));

        // -------------------------------------------------------
        // 3) Build payload
        //    NOTE: Do NOT send existing_attachments (backend returns strings only)
        // -------------------------------------------------------
        const payload = {
          consultant: {
            name: this.form.consultant.name,
            license_number: this.form.consultant.license_number,
            specialization: this.form.consultant.specialization,
            qualification: this.form.consultant.qualification,
            consultant_type: this.form.consultant.consultant_type,

            ...(this.form.consultant.image_base64
              ? {
                  image: {
                    base64: this.form.consultant.image_base64,
                    name: this.form.consultant.image_meta.name || "image.png",
                    type: this.form.consultant.image_meta.type || "image/png",
                    size: this.form.consultant.image_meta.size || 0,
                  },
                }
              : {}),

            // ✅ NEW uploads + replacements are both here
            attachments: attachmentsEncoded,

            // ✅ remove old files (including replaced ones)
            removed_existing_attachments: removed,
            existing_attachments_updates: existingTitlesUpdates,
          },

          address: {
            city: this.form.address.city,
            district: this.form.address.district,
            street: this.form.address.street,
            building_number: this.form.address.building,
            additional_number: this.form.address.additional,
            postal_code: this.form.address.postalCode,
            short_address: this.form.address.shortAddress,
          },

          bank: {
            account_holder_name: this.form.bank.holder,
            account_number: this.form.bank.iban,
            bank_name: this.form.bank.name,
          },
        };

        // -------------------------------------------------------
        // 4) Call API
        // NOTE: You are using POST /consultants (create).
        // If you want update, replace it with PUT/PATCH /consultants/{id}.
        // -------------------------------------------------------
        const res = await api.post("/consultants", payload, {
          headers: { Accept: "application/json" },
        });

        // -------------------------------------------------------
        // 5) Refresh local state
        // -------------------------------------------------------
        const updated = res?.data?.data ?? res?.data;
        this.user = updated?.consultant ?? updated;
        this.fillFormFromUser(true);

        // clear new image + new attachments
        this.form.consultant.image_base64 = "";
        this.form.consultant.attachments = [];

        // refresh existing attachments from updated user
        const rawExisting =
          this.user?.existing_attachments ??
          this.user?.attachments ??
          this.user?.consultant?.existing_attachments ??
          this.user?.consultant?.attachments ??
          this.user?.office?.attachments ??
          [];

        this.form.consultant.existing_attachments = (rawExisting || [])
          .map(this.normalizeExistingAttachment)
          .filter((a) => a.url);

        // clear removed list after successful save
        this.form.consultant.removed_existing_attachments = [];
        await this.initUserFromApi();
        this.showToast(this.t("account_saved_success"), "success");
      } catch (err) {
        const res = err.response?.data;
        if (res?.errors) {
          const msg = Object.values(res.errors)
            .flat()
            .filter(Boolean)
            .join("\n");
          this.showToast(msg, "error");
        } else {
          this.showToast(
            res?.message || this.t("account_saved_failed"),
            "error"
          );
        }
      } finally {
        this.saving = false;
      }
    },
    async savePassword() {
      if (this.passwordSaving || this.loading) return;
      this.passwordSaving = true;

      try {
        const url = "/users/updatePassword";
        const bodyData = {
          old_password: this.passwordForm.current_password,
          password: this.passwordForm.password,
          password_confirmation: this.passwordForm.password_confirmation,
        };

        const { data: res } = await api.patch(url, bodyData, {
          headers: { Accept: "application/json" },
        });

        this.showToast(
          res?.message || this.t("security_password_success"),
          "success"
        );

        this.passwordForm.current_password = "";
        this.passwordForm.password = "";
        this.passwordForm.password_confirmation = "";
      } catch (err) {
        const res = err.response?.data;
        if (res?.errors) {
          const msg = Object.values(res.errors)
            .flat()
            .filter(Boolean)
            .join("\n");
          this.showToast(msg, "error");
        } else {
          this.showToast(
            res?.message || this.t("security_password_failed"),
            "error"
          );
        }
      } finally {
        this.passwordSaving = false;
      }
    },
    clearProfileImage() {
      this.form.consultant.image_base64 = "";
      this.form.consultant.image_meta = { name: "", type: "", size: 0 };
      this.form.consultant.logo_url = this.user?.logo ?? "";
    },
    async onImageChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      this.form.consultant.image_base64 = String(dataUrl).split(",")[1] || "";
      this.form.consultant.image_meta = {
        name: file.name,
        type: file.type,
        size: file.size,
      };

      this.form.consultant.logo_url = dataUrl;
    },
    async fileToBase64(file) {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const base64 = String(dataUrl).split(",")[1] || "";
      return {
        base64,
        name: file.name,
        type: file.type || "application/octet-stream",
        size: file.size || 0,
      };
    },

    normalizeExistingAttachment(x) {
      if (!x) return { url: "", title: "", id: null, _replacement: null };

      if (typeof x === "string") {
        const clean = this.cleanFilename(x);
        return { url: x, title: clean, id: null, _replacement: null };
      }

      const url =
        x.url || x.path || x.link || x.file_url || x.download_url || "";

      const rawTitle =
        x.title ||
        x.name ||
        x.original_name ||
        x.filename ||
        this.cleanFilename(url);

      return {
        url,
        title: this.removeExtension(rawTitle),
        id: x.id ?? null,
        _replacement: null,
      };
    },
    cleanFilename(value) {
      if (!value) return "";
      const name = decodeURIComponent(
        (String(value).split("/").pop() || "").split("?")[0]
      );
      return this.removeExtension(name);
    },
    removeExtension(name) {
      if (!name) return "";
      return String(name).replace(/(\.[a-z0-9]+)+$/i, "");
    },

    normalizeNewAttachmentFile(file) {
      return {
        file,
        // ✅ default title from file name (same spirit as step 4)
        title: file?.name ? file.name : "",
      };
    },

    onAttachmentsChange(e) {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      const mapped = files.map(this.normalizeNewAttachmentFile);

      this.form.consultant.attachments = [
        ...(this.form.consultant.attachments || []),
        ...mapped,
      ];

      e.target.value = "";
    },

    removeNewAttachment(index) {
      const arr = this.form.consultant.attachments || [];
      arr.splice(index, 1);
      this.form.consultant.attachments = [...arr];
    },

    removeExistingAttachment(attOrUrl) {
      const url = typeof attOrUrl === "string" ? attOrUrl : attOrUrl?.url;

      this.form.consultant.existing_attachments = (
        this.form.consultant.existing_attachments || []
      ).filter((x) => x.url !== url);

      if (
        url &&
        !this.form.consultant.removed_existing_attachments.includes(url)
      ) {
        this.form.consultant.removed_existing_attachments.push(url);
      }
    },
    getFilename(url) {
      if (!url) return "";
      try {
        return decodeURIComponent((url.split("/").pop() || "").split("?")[0]);
      } catch (e) {
        return (url.split("/").pop() || "").split("?")[0];
      }
    },
    absoluteUrl(u) {
      if (!u) return "";
      if (u.startsWith("http://") || u.startsWith("https://")) return u;

      const base = (api.defaults?.baseURL || "").replace(/\/$/, "");
      const path = u.startsWith("/") ? u : `/${u}`;
      return base ? `${base}${path}` : u;
    },

    getConsultantIdFromAuthCookie() {
      try {
        const raw = this.getCookie("auth_user");
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        // adjust these keys based on your auth payload
        return (
          parsed?.consultant?.id ||
          parsed?.user?.consultant_id ||
          parsed?.id ||
          null
        );
      } catch (e) {
        return null;
      }
    },

    getCookie(name) {
      const match = document.cookie.match(
        new RegExp(
          "(?:^|; )" +
            name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") +
            "=([^;]*)"
        )
      );
      return match ? decodeURIComponent(match[1]) : null;
    },
  },
};
</script>

